import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import useAuth from '../../auth/useAuth';
import { getUserProfile } from '../../services/userService';
import './ProfilePage.css';

const LABELS = {
  user: 'User',
  admin: 'Admin',
};

const formatDate = (value) =>
  value
    ? new Intl.DateTimeFormat(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short',
      }).format(new Date(value))
    : 'Not available';

const formatSupportDate = (value) => {
  if (!value) return '';

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return '';

  return new Intl.DateTimeFormat('en', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
};

const ACTIVE_SUBSCRIPTION_STATUSES = new Set(['active', 'trialing']);
const SUPPORT_PLAN_LABELS = {
  free: 'Free',
  supporter: 'Supporter',
  premium: 'Supporter Plus',
};

const getPlanSupportDetails = (profile) => {
  const planLabel = SUPPORT_PLAN_LABELS[profile.plan] || SUPPORT_PLAN_LABELS.free;
  const recurringPlan = profile.stripeSubscriptionPlan;
  const recurringLabel = SUPPORT_PLAN_LABELS[recurringPlan];
  const hasActiveRecurring =
    recurringLabel && ACTIVE_SUBSCRIPTION_STATUSES.has(profile.stripeSubscriptionStatus);
  const currentPeriodEnd = formatSupportDate(profile.stripeCurrentPeriodEnd);
  const details = [];

  if (hasActiveRecurring) {
    if (profile.stripeCancelAtPeriodEnd) {
      if (currentPeriodEnd) details.push(`${recurringLabel} until ${currentPeriodEnd}`);
    } else if (currentPeriodEnd) {
      details.push(`Next payment: ${currentPeriodEnd}`);
    }

    return {
      actionLabel: 'Change subscription',
      details,
      planLabel,
      status: profile.stripeCancelAtPeriodEnd ? 'Subscription canceled' : '',
    };
  }

  details.push(
    'ASiJoLab is free to use. Supporting the project is voluntary and does not unlock additional functionality.'
  );

  return {
    actionLabel: 'Support ASiJoLab',
    details,
    planLabel,
    status: '',
  };
};

const ProfilePage = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [signOutError, setSignOutError] = useState('');
  const [signingOut, setSigningOut] = useState(false);

  useEffect(() => {
    let active = true;

    getUserProfile()
      .then((user) => {
        if (active) setProfile(user);
      })
      .catch((error) => {
        if (!active) return;

        if (error?.response?.status === 401) {
          navigate('/login', { replace: true });
          return;
        }

        setLoadError('Unable to load your profile. Please try again later.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [navigate]);

  const handleSignOut = async () => {
    setSignOutError('');
    setSigningOut(true);

    try {
      await logout();
      navigate('/login');
    } catch {
      setSignOutError('Sign-out failed.');
    } finally {
      setSigningOut(false);
    }
  };

  if (loading) {
    return <main className="profilePage"><p>Loading profile...</p></main>;
  }

  if (loadError) {
    return <main className="profilePage"><p className="profileError">{loadError}</p></main>;
  }

  const details = [
    ['Email', profile.email],
    ['Role', LABELS[profile.role] || profile.role],
    ['Member since', formatDate(profile.createdAt)],
    ['Last sign in', formatDate(profile.lastLoginAt)],
  ];
  const supportDetails = getPlanSupportDetails(profile);

  return (
    <main className="profilePage">
      <section className="profileCard">
        <h1>Profile</h1>

        <section className="profileSection">
          <h2>Account details</h2>
          <dl className="profileDetails">
            {details.map(([label, value]) => (
              <div key={label}>
                <dt>{label}</dt>
                <dd>{value}</dd>
              </div>
            ))}
          </dl>
        </section>

        <section className="profileSection planSupportSection">
          <div className="planSupportHeader">
            <div>
              <h2>Support</h2>
              {supportDetails.status && (
                <p className="planSupportStatus">{supportDetails.status}</p>
              )}
            </div>
            <Link className="planSupportAction" to="/support">
              {supportDetails.actionLabel}
            </Link>
          </div>

          <dl className="planSupportDetails">
            <div>
              <dt>Support level</dt>
              <dd>{supportDetails.planLabel}</dd>
            </div>
          </dl>

          {supportDetails.details.length > 0 && (
            <div className="planSupportNotes">
              {supportDetails.details.map((detail) => (
                <p key={detail}>{detail}</p>
              ))}
            </div>
          )}
        </section>

        <section className="profileSection">
          <h2>Account settings</h2>
          <div className="profileSettings">
            <Link to="/profile/change-email">Change email</Link>
            <Link to="/profile/change-password">Change password</Link>
          </div>
        </section>

        {signOutError && <p className="profileError" role="alert">{signOutError}</p>}

        <button
          className="profileSignOut"
          type="button"
          onClick={handleSignOut}
          disabled={signingOut}
        >
          {signingOut ? 'Signing out...' : 'Sign out'}
        </button>
      </section>
    </main>
  );
};

export default ProfilePage;
